// The span of a day that time-placed components draw along (round 2): the day board, and any other
// timeline (a timesheet's day). TimeScale, NowLine, BoardLane and the timesheet's rows read it, so none of
// them assumes it's on the board. A Board provides it; anything else wraps its rows in TimeSpanProvider.
import { createContext, useContext, type ReactNode } from 'react';

export type Density = 'comfortable' | 'compact';

export interface TimeSpan {
  /** The first and last hour drawn, in minutes from midnight (whole hours). */
  from: number;
  to: number;
  /** Now, in minutes from midnight, or null on any other day. */
  now: number | null;
  density: Density;
  /** Nothing moves (the board while the company's records load, BL-158 5; a locked pay period). */
  readOnly: boolean;
}

const SpanContext = createContext<TimeSpan>({
  from: 7 * 60,
  to: 19 * 60,
  now: null,
  density: 'comfortable',
  readOnly: false,
});

export const useTimeSpan = () => useContext(SpanContext);

/** A span from loose times: from and to rounded out to whole hours, at least one hour long. */
export function timeSpan({
  from = 7 * 60,
  to = 19 * 60,
  now = null,
  density = 'comfortable',
  readOnly = false,
}: Partial<TimeSpan>): TimeSpan {
  const start = Math.floor(from / 60) * 60;
  return { from: start, to: Math.max(Math.ceil(to / 60) * 60, start + 60), now, density, readOnly };
}

export function TimeSpanProvider({ children, ...span }: Partial<TimeSpan> & { children: ReactNode }) {
  return <SpanContext.Provider value={timeSpan(span)}>{children}</SpanContext.Provider>;
}

/** Where a time falls along the span, 0 to 100 (clamped to it). */
export function spanPct(span: Pick<TimeSpan, 'from' | 'to'>, minute: number): number {
  const m = Math.min(Math.max(minute, span.from), span.to);
  return ((m - span.from) / (span.to - span.from)) * 100;
}

/** Where something from `a` to `b` sits along the span: its inline start and size, in percent. */
export function spanPos(span: Pick<TimeSpan, 'from' | 'to'>, a: number, b: number) {
  return {
    insetInlineStart: `${spanPct(span, a)}%`,
    inlineSize: `${Math.max(spanPct(span, b) - spanPct(span, a), 0)}%`,
  };
}

/** The hours in the span, as the minutes each starts at. */
export function spanHours(span: Pick<TimeSpan, 'from' | 'to'>): number[] {
  const out: number[] = [];
  for (let m = Math.ceil(span.from / 60) * 60; m <= span.to; m += 60) out.push(m);
  return out;
}
